import comp from '../components/barrel';

const SECTION_ORDER = [
  'ADs_PersonalDetails',
  'ADs_MaritalStatus',
  'ADs_ChildrenDetails',
  'ADs_Devise',
  'ADs_Heirs',
  'ADs_Trust',
  'ADs_Guardian',
  'ADs_Equity',
];

const DRAFT_UPLOADED_STATUS_UUID = 'cd9353c9-d692-4751-b6f3-0a4352653284';

const REVISION_FIELD_BY_SECTION = {
  ADs_PersonalDetails: 'personal_details',
  ADs_MaritalStatus: 'marital_status',
  ADs_ChildrenDetails: 'children_details',
  ADs_Devise: 'special_bequests',
  ADs_Heirs: 'heirs_to_residue',
  ADs_Trust: 'trust_and_coexecutor',
  ADs_Guardian: 'guardian',
  ADs_Equity: 'assets_and_liabilities',
};

const val = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const joinAddress = (parts) => {
  const text = parts.filter(Boolean).join(', ');
  return text || '—';
};

const personName = (person) => {
  if (!person) return '—';
  return (
    person.full_name ||
    [person.f_name, person.s_name].filter(Boolean).join(' ') ||
    person.name ||
    '—'
  );
};

const childName = (child) =>
  child?.full_name ||
  [child?.f_name, child?.s_name].filter(Boolean).join(' ') ||
  child?.children ||
  '—';

const rowsHtml = (rows) => {
  if (!rows.length) {
    return `<p class="text-sm text-gray-500 mb-0">No details recorded.</p>`;
  }
  return `
    <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-0">
      ${rows
        .map(
          ([label, value]) => `
        <div class="min-w-0">
          <dt class="text-xs uppercase tracking-wide text-gray-500">${label}</dt>
          <dd class="text-sm text-gray-800 break-words whitespace-pre-line mb-0">${val(value)}</dd>
        </div>`
        )
        .join('')}
    </dl>
  `;
};

const listBlock = (title, items) => {
  if (!items?.length) return '';
  return `
    <div class="mb-3">
      <p class="text-sm font-medium text-gray-700 mb-1">${title}</p>
      <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1 mb-0">
        ${items.map((item) => `<li>${item}</li>`).join('')}
      </ul>
    </div>
  `;
};

const formatPersonal = (detail) => {
  const parties = ['party1', 'party2', 'party3', 'party4', 'party5']
    .map((key) => detail?.[key])
    .filter(Boolean);

  if (!parties.length) return rowsHtml([]);

  return parties
    .map((party, index) => {
      const rows = [
        ['Name', personName(party)],
        ['Email', party.email_address],
        ['Cell', party.cell_nr],
        ['ID number', party.id_nr],
        ['Passport', party.passport_nr],
        [
          'Physical address',
          joinAddress([
            party.physical_address,
            party.physical_suburb,
            party.physical_town,
            party.physical_code,
          ]),
        ],
      ];
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          <p class="text-sm font-medium text-gray-700 mb-2">Party ${index + 1}</p>
          ${rowsHtml(rows)}
        </div>
      `;
    })
    .join('');
};

const formatMarital = (detail) => {
  const parties = ['party1', 'party2', 'party3', 'party4', 'party5']
    .map((key) => detail?.[key])
    .filter(Boolean);

  if (!parties.length) return rowsHtml([]);

  return parties
    .map((party, index) => {
      const rows = [
        ['Name', personName(party)],
        ['Marital status', party.long_text || party.marriage_uuid],
      ];
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          ${rowsHtml(rows)}
        </div>
      `;
    })
    .join('');
};

const formatChildren = (detail) => {
  const blocks = [
    listBlock(
      'Joint children',
      (detail?.present || []).map((c) => childName(c))
    ),
    listBlock(
      'Party 1 children',
      (detail?.party1 || []).map((c) => childName(c))
    ),
    listBlock(
      'Party 2 children',
      (detail?.party2 || []).map((c) => childName(c))
    ),
    listBlock(
      'Other children',
      (detail?.other || []).map((c) => childName(c))
    ),
  ].filter(Boolean);

  if (!blocks.length) {
    return rowsHtml([['Children', detail?.children_yn === false ? 'No children' : null]]);
  }

  return `
    ${rowsHtml([['Has children', detail?.children_yn]])}
    <div class="mt-3">${blocks.join('')}</div>
  `;
};

const formatDevise = (detail) => {
  const wishes = (detail?.wishes || [])
    .map((w) => w.wish || w.instruction)
    .filter(Boolean);
  const bequests = (detail?.bequests || [])
    .map((b) => b.instruction || b.instructions)
    .filter(Boolean);

  return `
    ${listBlock('Wishes', wishes) || rowsHtml([['Wishes', null]])}
    ${listBlock('Special bequests', bequests) || rowsHtml([['Special bequests', null]])}
  `;
};

const formatHeirs = (detail) => {
  const heirNames = [
    ...(detail?.heir_list?.heirs || []),
    ...(detail?.heir_list?.children || []),
    ...(detail?.heir_list?.company || []),
  ]
    .map((h) => h.full_name || h.name || [h.f_name, h.s_name].filter(Boolean).join(' '))
    .filter(Boolean);

  const obliterations = [
    ...(detail?.obliteration_list?.heirs || []),
    ...(detail?.obliteration_list?.company || []),
  ]
    .map((h) => h.full_name || h.name || [h.f_name, h.s_name].filter(Boolean).join(' '))
    .filter(Boolean);

  const notes = (detail?.heir_notes || []).map((n) => n.note).filter(Boolean);

  return `
    ${rowsHtml([
      ['Party 1 sole heir', detail?.party1?.heir_name],
      ['Party 2 sole heir', detail?.party2?.heir_name],
    ])}
    <div class="mt-3">
      ${listBlock('Heirs', heirNames)}
      ${listBlock('Family obliteration', obliterations)}
      ${listBlock('Heir notes', notes)}
    </div>
  `;
};

const formatTrust = (detail) => {
  const trust = detail?.trust || {};
  const trustees = (detail?.trustees || []).map(
    (t) =>
      [t.fname || t.f_name, t.sname || t.s_name].filter(Boolean).join(' ') ||
      t.notes ||
      'Trustee'
  );
  const coexecutors = (detail?.coexecutors || []).map(
    (c) =>
      [c.fname || c.f_name, c.sname || c.s_name].filter(Boolean).join(' ') ||
      c.notes ||
      'Co-executor'
  );

  return `
    ${rowsHtml([
      ['Inheritance in trust', trust.inheritance_in_trust_yn],
      ['Inheritance age', trust.inheritance_age],
      ['Assets to guardian', trust.assets_to_guardian_yn],
      ['Assets to trust', trust.assets_to_trust_yn],
      ['Notes', trust.notes],
    ])}
    <div class="mt-3">
      ${listBlock('Trustees', trustees)}
      ${listBlock('Co-executors', coexecutors)}
    </div>
  `;
};

const formatGuardian = (detail) => {
  const guardians = (detail?.guardian_list || []).map((g) => {
    const name = [g.f_name, g.s_name].filter(Boolean).join(' ') || 'Guardian';
    const rel = [g.rel_party1, g.rel_party2].filter(Boolean).join(' / ');
    return rel ? `${name} (${rel})` : name;
  });

  return `
    ${rowsHtml([['Guardian required', detail?.guardian_yn]])}
    <div class="mt-3">${listBlock('Guardians', guardians)}</div>
  `;
};

const formatEquity = (detail) => {
  const rows = Array.isArray(detail) ? detail : [];
  if (!rows.length) return rowsHtml([]);

  return rows
    .map((item, index) => {
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          ${rowsHtml([
            ['Category', item.estate_text],
            ['Description', item.estate_desc],
            ['Asset value', item.ass_val],
            ['Liability value', item.liab_val],
            ['Share value', item.share_val],
          ])}
        </div>
      `;
    })
    .join('');
};

const formatSectionDetail = (key, detail) => {
  switch (key) {
    case 'ADs_PersonalDetails':
      return formatPersonal(detail);
    case 'ADs_MaritalStatus':
      return formatMarital(detail);
    case 'ADs_ChildrenDetails':
      return formatChildren(detail);
    case 'ADs_Devise':
      return formatDevise(detail);
    case 'ADs_Heirs':
      return formatHeirs(detail);
    case 'ADs_Trust':
      return formatTrust(detail);
    case 'ADs_Guardian':
      return formatGuardian(detail);
    case 'ADs_Equity':
      return formatEquity(detail);
    default:
      return rowsHtml([]);
  }
};

export default {
  application_uuid: null,
  h_application_id: null,
  editable: false,
  sections: [],
  currentRevision: null,
  async prepare() {
    this.application_uuid = await thsPage.vars.application_uuid;
    const vars = {};
    vars.application_uuid = this.application_uuid;
    this.recipe.methods.push(await O.mPOST('application/get-application-detail', vars));
  },
  async render() {
    const rec = this.recipe.methods;

    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },
  async loadPage(data) {
    const appProgressData = data['application/get-application-detail'].data;

    this.editable = appProgressData.editable;

    if (this.editable) {
      await this.buildSubMenu(appProgressData);
    } else {
      await this.loadDraftingInProgressView();
    }
  },
  async loadDraftingInProgressView() {
    U.unhide('lockPage');

    const vars = { application_uuid: this.application_uuid };
    const res = await O.PREP([
      await O.mPOST('application/get-application-sections-summary', vars),
      await O.mPOST('application/get-application-revision-current', vars),
      await O.mPOST('application/get-app-status', vars),
    ]);

    const summary = res['application/get-application-sections-summary']?.data;
    this.sections = Array.isArray(summary?.sections) ? summary.sections : [];
    this.h_application_id = summary?.h_application_id ?? null;
    this.currentRevision = res['application/get-application-revision-current']?.data || null;

    const statusData = res['application/get-app-status']?.data || {};
    this.appStatusUuid = statusData.application_status_uuid || statusData.status_uuid || null;
    this.appStatus = statusData.short_text || null;
    this.blockDraftRevision =
      this.appStatusUuid === DRAFT_UPLOADED_STATUS_UUID || this.appStatus === 'Draft uploaded';

    this.revisionRows = [];
    if (this.blockDraftRevision) {
      try {
        const revisionsRes = await O.PREP([
          await O.mPOST('application/get-application-revisions', vars),
        ]);
        const rows = revisionsRes['application/get-application-revisions']?.data;
        this.revisionRows = Array.isArray(rows) ? rows : [];
      } catch (error) {
        this.revisionRows = [];
      }
    }

    this.renderDraftingSections();
    this.updateRevisionButtonState();
  },
  getRevisionNoteForSection(sectionKey) {
    if (!this.currentRevision) return '';
    const field = REVISION_FIELD_BY_SECTION[sectionKey];
    if (!field) return '';
    const note = this.currentRevision[field];
    return note == null ? '' : String(note);
  },
  getRevisionNotesForSection(sectionKey) {
    const field = REVISION_FIELD_BY_SECTION[sectionKey];
    if (!field || !Array.isArray(this.revisionRows)) return [];

    return this.revisionRows
      .map((row) => {
        const note = row?.[field];
        if (note == null || !String(note).trim()) return null;
        return String(note).trim();
      })
      .filter(Boolean);
  },
  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },
  renderDraftingSections() {
    const container = U.id('draftingSections');
    if (!container) return;
    container.innerHTML = '';

    const byKey = {};
    this.sections.forEach((section) => {
      byKey[section.key] = section;
    });

    SECTION_ORDER.forEach((key, index) => {
      const section = byKey[key] || {
        key,
        label: key,
        detail: null,
      };
      const order = index + 1;
      const revisionNote = this.getRevisionNoteForSection(key);
      let revisionBlock;

      if (this.blockDraftRevision) {
        const sectionNotes = this.getRevisionNotesForSection(key);
        const notesHtml = sectionNotes.length
          ? `
            <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1 mb-3">
              ${sectionNotes.map((note) => `<li class="whitespace-pre-line">${this.escapeHtml(note)}</li>`).join('')}
            </ul>`
          : '';
        revisionBlock = `
          ${notesHtml}
          <p class="text-sm text-gray-700 mb-0">
            As there is already a draft on record, if you need to make changes to this section, request a redraft and update the details.
          </p>`;
      } else {
        revisionBlock = `
          <div class="input-group col-span-full mb-0">
            <label for="revision_${section.key}">Request revision to this section</label>
            <textarea
              id="revision_${section.key}"
              name="revision_${section.key}"
              data-section-key="${section.key}"
              data-section-label="${section.label}"
              class="revision-note"
              rows="3"
            ></textarea>
          </div>`;
      }

      const sectionEl = U.createHTMLElementFromString(`
        <section class="border border-gray-200 rounded-lg p-4 bg-white" data-section-key="${section.key}">
          <div class="flex items-center gap-3 mb-3">
            <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-sm font-medium">${order}</span>
            <h5 class="text-base font-semibold text-gray-800 mb-0">${section.label}</h5>
          </div>
          <div class="section-detail mb-4">
            ${formatSectionDetail(section.key, section.detail)}
          </div>
          ${revisionBlock}
        </section>
      `);

      if (!this.blockDraftRevision) {
        const textarea = sectionEl.querySelector('textarea.revision-note');
        if (textarea) {
          textarea.value = revisionNote;
        }
      }

      container.appendChild(sectionEl);
    });

    if (!this.blockDraftRevision) {
      container.querySelectorAll('textarea.revision-note').forEach((textarea) => {
        textarea.addEventListener('input', () => this.updateRevisionButtonState());
      });
    }
  },
  updateRevisionButtonState() {
    const btn = U.id('requestDraftRevisionBtn');
    if (!btn) return;

    if (this.blockDraftRevision) {
      btn.classList.add('hide');
      btn.disabled = true;
      return;
    }

    btn.classList.remove('hide');
    const notes = [...document.querySelectorAll('#draftingSections textarea.revision-note')];
    const enabled = notes.some((el) => (el.value || '').trim().length >= 20);
    btn.disabled = !enabled;
  },
  collectRevisionSections() {
    return [...document.querySelectorAll('#draftingSections textarea.revision-note')].map(
      (el) => ({
        key: el.dataset.sectionKey,
        label: el.dataset.sectionLabel,
        note: (el.value || '').trim(),
      })
    );
  },
  async requestDraftRevision() {
    if (this.blockDraftRevision) {
      return;
    }

    const sections = this.collectRevisionSections();
    const hasMinLength = sections.some((section) => section.note.length >= 20);
    if (!hasMinLength) {
      this.updateRevisionButtonState();
      return;
    }

    const vars = {
      application_uuid: this.application_uuid,
      h_application_id: this.h_application_id,
      sections,
    };

    if (this.currentRevision?.application_revision_uuid) {
      vars.application_revision_uuid = this.currentRevision.application_revision_uuid;
    }

    try {
      const response = await O.PREP([await O.mPOST('application/ups-application-revision', vars)]);
      const result = response['application/ups-application-revision'];
      if (result) {
        await comp.toast('Revision recorded');
        this.currentRevision = result.data || this.currentRevision;
        this.updateRevisionButtonState();
      }
    } catch (error) {
      // O.PREP surfaces errors via shared handling
    }
  },
  async buildSubMenu(appProgressData) {
    U.unhide('subTabs');

    let pages = appProgressData.pages;
    if (pages) {
      Object.values(pages).forEach((page) => {
        thsPage.pageProgress(page);
      });
    }

    const activePage = appProgressData.bookmark.page;
    thsPage.changeSideNavTab(activePage);
  },
};
